import { faClone, faEdit, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import React, { Fragment, useCallback, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from 'react-router-dom';
import * as Api from "../../../../../api";
import { OrderCategory, OrderCostingVersion, TApiOrderDetails, TProductCost } from '../../../../../api/endpoints/Order';
import { TOrderProduct } from '../../../../../api/endpoints/OrderProduct';
import { TBcType } from '../../../../../api/endpoints/Subset';
import IconButton from '../../../../../components/Buttons/IconButton';
import Col from '../../../../../components/Common/Col';
import Row from '../../../../../components/Common/Row';
import ProductDetails from "../../../../../components/ProductDetails";
import OrderPolicy from "../../../../../policies/OrderPolicy";
import { paths } from '../../../../../router';
import { selectUser } from "../../../../../store/selectors/UserSelectors";
import { formatBinaryCapacityUnit } from "../../../../../utils/Format";

interface IProps {
  datasetName: string;
  order: TApiOrderDetails<OrderCategory.REALTIME>;
  orderCostingVersions: Array<OrderCostingVersion>;
  product: TOrderProduct<OrderCategory.REALTIME>;
  cost?: TProductCost;
  bcType: TBcType;
  onProductRemove: (product: TOrderProduct<OrderCategory.REALTIME>) => void;
  onProductDuplicate: (product: TOrderProduct<OrderCategory.REALTIME>) => void;
}

const RealtimeProductDetails = React.forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const { order,
    orderCostingVersions,
    product,
    cost,
    datasetName,
    bcType,
    onProductRemove,
    onProductDuplicate
  } = props;

  const set = {
    name: datasetName
  };

  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  const user = useSelector(selectUser);
  const history = useHistory();
  const dispatch = useDispatch();

  const onDuplicate = useCallback(() => {
    setIsDuplicating(true);

    Api.OrderProduct.duplicate(product.order_id, product.id).then((newProduct) => {
      onProductDuplicate(newProduct);
    }).finally(() => {
      setIsDuplicating(false);
    })
  }, [onProductDuplicate, product]);

  const onProductEdit = useCallback(() => {
    history.push(paths.realtimeOrderProduct(order.id, product.id));
  }, [history, order, product.id])

  const onRemove = useCallback(() => {
    setIsRemoving(true);

    Api.OrderProduct.remove(product.order_id, product.id).then(() => {
      onProductRemove(product);
    }).finally(() => {
      setIsRemoving(false);
    });
  }, [dispatch, order, product.id]);

  return (
    <Row ref={ref}>
      <Col sm={12} md={cost ? 8 : 12}>
        <ProductDetails
          productData={product.data}
          timestamp={order.updated}
          set={set}
          bcType={bcType}
          actions={
            <Fragment>
              {
                OrderPolicy.canAddProduct(user, order, orderCostingVersions) &&
                <IconButton loading={isDuplicating} icon={faClone} onClick={onDuplicate} title="Duplicate" />
              }
              {
                OrderPolicy.canUpdateProduct(user, order) &&
                <IconButton icon={faEdit} onClick={onProductEdit} title="Edit" />
              }
              {
                OrderPolicy.canRemoveProduct(user, order) &&
                <IconButton loading={isRemoving} icon={faTrashAlt} onClick={onRemove} variant="danger" title="Remove" />
              }
            </Fragment>
          }
        />
      </Col>

      {
        cost &&
        <Col sm={12} md={4}>
          <Row className="table__details--item-header">
            <span className="table__details--item-title">Cost</span>
          </Row>

          <div className="table__details--content">
            <Row>
              <Col sm={4} md={5} className="table__details--content-column-details">
                <div>Volume daily</div>
              </Col>

              <Col sm={8} md={7} className="table__details--content-column-details">
                {
                  formatBinaryCapacityUnit(cost.average_daily_volume)
                }
              </Col>
            </Row>
          </div>
        </Col>
      }
    </Row>
  );
});

RealtimeProductDetails.displayName = "RealtimeProductDetails";

export default RealtimeProductDetails;
